import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
  CircularProgress,
} from "@mui/material";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import AiIcon from "../../assets/medicine_icon_active.svg";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArticleIcon from "@mui/icons-material/Article";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
// Styled components
const SectionCard = styled(Paper)(({ theme }) => ({
  padding: "20px",
  borderRadius: "16px",
  border: "1px solid #E5E7EB",
  backgroundColor: "#FFFFFF",
  height: "100%",
  fontFamily: "Lato",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  color: "#6B7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: "Lato",
}));

const VitalBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#F9FAFB",
  borderRadius: "12px",
  padding: "12px 16px",
  marginBottom: "12px",
  fontFamily: "Lato",
}));

const VitalLabel = styled(Typography)(({ theme }) => ({
  fontSize: "11px",
  color: "#6B7280",
  fontWeight: 500,
  textTransform: "uppercase",
  marginBottom: "4px",
  fontFamily: "Lato",
}));

const VitalValue = styled(Typography)(({ theme }) => ({
  fontSize: "18px",
  fontWeight: 600,
  color: "#111827",
  fontFamily: "Lato",
}));

const DiagnosisChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#0097B2",
  color: "#ffff",
  borderRadius: "8px",
  fontWeight: 600,
  fontSize: "14px",
  padding: "6px 4px",
  fontFamily: "Lato",
  height: "auto",
  "& .MuiChip-label": {
    padding: "6px 12px",
  },
}));

const SymptomChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#F3F4F6",
  color: "#374151",
  borderRadius: "8px",
  fontWeight: 400,
  fontSize: "13px",
  margin: "4px",
  height: "auto",
  fontFamily: "Lato",
  "& .MuiChip-label": {
    padding: "6px 12px",
  },
}));



const PatientSummaryDialog = ({
  openSummaryModal,
  handleCloseSummary,
  voiceSummary,
  isFetching,
  appointmentData
}) => {
  // const [symptoms, setSymptoms] = useState(
  //   voiceSummary?.payload?.summary_json?.diagnosis?.identified_symptoms || []
  // );

  const summaryData = voiceSummary?.payload?.ai_summary;
  const clinicalSummary = summaryData?.clinical_summary;
  const diagnosis = summaryData?.diagnosis;
  const prescriptions = summaryData?.ai_suggested_prescription || [];

  // Mock vitals data (can be replaced with actual data from API)
  const vitals = {
    temperature: appointmentData?.fever || "-",
    bp_sys: appointmentData?.bp_s || "-",
    bp_dia: appointmentData?.bp_d || "-",
    bmi: appointmentData?.bmi || "-",
  };

  return (
    <Dialog
      open={openSummaryModal}
      onClose={handleCloseSummary}
      // maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          maxWidth: "1500px",
          background: "#F8FAFC",
          fontFamily: "Lato",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          p: 3,
          pb: 2,
          borderBottom: "1px solid #E5E7EB",
          fontFamily: "Lato",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              backgroundColor: "#0097B240",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AutoAwesomeIcon
              style={{ width: 28, height: 28 }}
              sx={{ color: "#0097B2" }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: "18px",
                color: "#111827",
                fontFamily: "Lato",
              }}
            >
              AI Pre-Consultation Summary
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                fontSize: "13px",
                fontFamily: "Lato",
              }}
            >
              Review  AI findings before starting.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleCloseSummary} size="small">
          <CloseIcon sx={{ color: "#6B7280" }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, backgroundColor: "#F9FAFB" }}>
        {isFetching ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
              gap: 3,
            }}
          >
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <CircularProgress
                size={60}
                thickness={3}
                sx={{ color: "#0097B2" }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: "absolute",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AutoAwesomeIcon sx={{ color: "#0097B2", fontSize: 24 }} />
              </Box>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#111827",
                  fontFamily: "Lato",
                  mb: 1,
                }}
              >
                Processing Conversation...
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#6B7280",
                  fontFamily: "Lato",
                  maxWidth: "350px",
                  mx: "auto",
                  lineHeight: 1.5,
                }}
              >
                Transcribing nurse notes, extracting vitals, and preparing
                prescription draft.
              </Typography>
            </Box>
          </Box>
        ) : voiceSummary?.payload ? (
          <>
            {/* Top Section - Vitals, Clinical Summary, Diagnosis */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Vitals Section */}
              <Grid item xs={12} md={3}>
                <SectionCard elevation={0}>
                  <SectionTitle>
                    <ThermostatIcon sx={{ fontSize: 16, color: "#0097B2" }} />
                    <Typography
                      fontWeight={700}
                      color={"black"}
                      fontSize={"16px"}
                      fontFamily={"Lato"}
                    >
                      {" "}
                      VITALS
                    </Typography>
                  </SectionTitle>

                  <VitalBox>
                    <VitalLabel>Temperature</VitalLabel>
                    <VitalValue>
                      <ThermostatIcon
                        sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }}
                      />
                      {vitals.temperature}
                    </VitalValue>
                  </VitalBox>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <VitalBox sx={{ mb: 1 }}>
                        <VitalLabel>BP (SYS)</VitalLabel>
                        <VitalValue sx={{ fontSize: "18px" }}>
                          {vitals.bp_sys}
                        </VitalValue>
                      </VitalBox>
                    </Grid>
                    <Grid item xs={6}>
                      <VitalBox sx={{ mb: 1 }}>
                        <VitalLabel>BP (DIA)</VitalLabel>
                        <VitalValue sx={{ fontSize: "18px" }}>
                          {vitals.bp_dia}
                        </VitalValue>
                      </VitalBox>
                    </Grid>
                  </Grid>

                  {/* <VitalBox>
                    <VitalLabel>BMI</VitalLabel>
                    <VitalValue>
                      <FavoriteIcon
                        sx={{
                          fontSize: 18,
                          verticalAlign: "middle",
                          mr: 0.5,
                          color: "red",
                        }}
                      />
                      {vitals.bmi}
                    </VitalValue>
                  </VitalBox> */}
                </SectionCard>
              </Grid>

              {/* Clinical Summary Section */}
              <Grid item xs={12} md={4.5}>
                <SectionCard elevation={0}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <SectionTitle>
                      <ArticleIcon sx={{ fontSize: 16, color: "#0097B2" }} />
                      CLINICAL SUMMARY
                    </SectionTitle>
                    {/* <IconButton size="small">
                      <EditIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                    </IconButton> */}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6B7280",
                        textTransform: "uppercase",
                        mb: 1,
                        fontFamily: "Lato",
                      }}
                    >
                      Chief Complaint
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#F9FAFB",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        marginBottom: "12px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#374151",
                          lineHeight: 1.6,
                          fontFamily: "Lato",
                        }}
                      >
                        {clinicalSummary?.chief_complaint ||
                          "No chief complaint recorded"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6B7280",
                        textTransform: "uppercase",
                        mb: 1,
                        fontFamily: "Lato",
                      }}
                    >
                      History of Present Illness
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#F9FAFB",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        marginBottom: "12px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#374151",
                          lineHeight: 1.6,
                          whiteSpace: "pre-line",
                          fontFamily: "Lato",
                        }}
                      >
                        {clinicalSummary?.history_of_present_illness ||
                          "No history recorded"}
                      </Typography>
                    </Box>
                  </Box>
                </SectionCard>
              </Grid>

              {/* Diagnosis Section */}
              <Grid item xs={12} md={4.5}>
                <SectionCard elevation={0}>
                  <SectionTitle>
                    <LocalHospitalIcon
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "4px",
                        color: "#0097B2",
                      }}
                    />
                    DIAGNOSIS
                  </SectionTitle>

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6B7280",
                        textTransform: "uppercase",
                        mb: 1,
                        fontFamily: "Lato",
                      }}
                    >
                      Provisional Diagnosis
                    </Typography>
                    <DiagnosisChip
                      label={
                        diagnosis?.provisional_diagnosis || "Not specified"
                      }
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6B7280",
                        textTransform: "uppercase",
                        mb: 1,
                        fontFamily: "Lato",
                      }}
                    >
                      Identified Symptoms
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      {diagnosis?.identified_symptoms?.map((symptom, index) => (
                        <SymptomChip key={index} label={symptom} />
                      ))}
                    </Box>
                    {/* <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#9CA3AF",
                        fontStyle: "italic",
                        mt: 1,
                      }}
                    >
                      + Add
                    </Typography> */}
                  </Box>
                </SectionCard>
              </Grid>
            </Grid>

            {/* AI Suggested Prescription Section */}
            <SectionCard elevation={0} sx={{ mb: 2, mt: 10 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "4px",
                    backgroundColor: "#DBEAFE",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={AiIcon}
                    alt="AI"
                    style={{ width: 12, height: 12 }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#6B7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontFamily: "Lato",
                  }}
                >
                  AI SUGGESTED PRESCRIPTION
                </Typography>
                <Chip
                  label="Based on Diagnosis"
                  size="medium"
                  sx={{
                    backgroundColor: "#0097B2",
                    color: "#ffff",
                    fontSize: "11px",
                    height: "24px",
                    fontWeight: 600,
                    fontFamily: "Lato",

                    ml: "auto",
                  }}
                />
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#6B7280",
                          textTransform: "uppercase",
                          border: "none",
                          fontFamily: "Lato",
                        }}
                      >
                        Medicine
                      </TableCell>
                      {/* <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#6B7280",
                          textTransform: "uppercase",
                          border: "none",
                          fontFamily: "Lato",
                        }}
                      >
                        Dosage
                      </TableCell> */}
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#6B7280",
                          textTransform: "uppercase",
                          border: "none",
                          fontFamily: "Lato",
                        }}
                      >
                        Remarks
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prescriptions.length > 0 ? (
                      prescriptions.map((prescription, index) => (
                        <TableRow
                          key={index}
                          sx={{
                            "&:last-child td": { border: 0 },
                            backgroundColor:
                              index % 2 === 0 ? "#FFFFFF" : "#F9FAFB",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: 700,
                              border: "none",
                              py: 2,
                              fontFamily: "Lato",
                            }}
                          >
                            {prescription.medicine_name}
                          </TableCell>
                          {/* <TableCell
                            sx={{
                              fontSize: "14px",
                              color: "#6B7280",
                              border: "none",
                              py: 2,
                              fontFamily: "Lato",
                            }}
                          >
                            {prescription.dosage || "-"}
                          </TableCell> */}
                          <TableCell
                            sx={{
                              fontSize: "13px",
                              color: "#6B7280",
                              fontStyle: "italic",
                              border: "none",
                              py: 2,
                              fontFamily: "Lato",
                            }}
                          >
                            {prescription.remarks}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          sx={{
                            textAlign: "center",
                            color: "#9CA3AF",
                            fontSize: "14px",
                            py: 3,
                            border: "none",
                            fontFamily: "Lato",
                          }}
                        >
                          No prescriptions suggested
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </SectionCard>

            {/* Footer with Confidence Score and Confirm Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pt: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: "#10B981",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#6B7280",
                    fontFamily: "Lato",
                  }}
                >
                  AI Confidence Score:{" "}
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#111827",
                      fontFamily: "Lato",
                    }}
                  >
                    94%
                  </span>
                </Typography>
              </Box>
              {/* <ConfirmButton
                variant="contained"
                onClick={handleConfirm}
                endIcon={
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      transform: "rotate(-45deg)",
                    }}
                  >
                    →
                  </Box>
                }
              >
                Confirm & Save Consultation
              </ConfirmButton> */}
            </Box>
          </>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ color: "#9CA3AF", fontFamily: "Lato" }}
            >
              No summary available
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PatientSummaryDialog;
